import React, { useEffect, useRef, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';
import { FaPlayCircle, FaPauseCircle, FaVolumeDown, FaVolumeMute } from "react-icons/fa";
import { goldColour, blueColour } from '../pages/Home';
import VisDiv from '../whitaijiFrontendLibrary/components/VisDiv';
import { FaStepForward, FaStepBackward } from 'react-icons/fa';
import useMediaQuery from '../hooks/useMediaQuery';


const LogMusicPlayer = ({songSource: initialSongSource, title: initialTitle, containerStyle, hideTestButtons, crossFadeSeconds, lyrics: initialLyrics, logSeconds=()=>{}, secondsOnly=true, songs}) => {
  const [currentLyric, setCurrentLyric] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState(null);
  const [currentSongIndex, setCurrentSongIndex] = useState(0);
  const [currentSongSource, setCurrentSongSource] = useState(initialSongSource);
  const [currentTitle, setCurrentTitle] = useState(initialTitle);
  const [currentLyrics, setCurrentLyrics] = useState(initialLyrics);
  const waveformRef = useRef(null);
  const wavesurferRef = useRef(null);
  const containerRef = useRef(null);
  const previousVolume = useRef(1);
  const screenSizeNotMobile = useMediaQuery("(min-width: 742px)")

  // Set initial song based on whether we have multiple songs or a single song
  useEffect(() => {
    if (songs && songs.length > 1 && songs[currentSongIndex]) {
      setCurrentSongSource(songs[currentSongIndex].soundSource);
      setCurrentTitle(songs[currentSongIndex].title);
      setCurrentLyrics(songs[currentSongIndex].lyrics);
    } else {
      setCurrentSongSource(initialSongSource);
      setCurrentTitle(initialTitle);
      setCurrentLyrics(initialLyrics);
    }
  }, [songs, currentSongIndex, initialSongSource, initialTitle, initialLyrics]);
  useEffect(() => {
    logSeconds(formatTime(currentTime, secondsOnly))
},[currentTime])

  useEffect(() => {
    // Check if waveformRef.current exists before initializing WaveSurfer
    if (!waveformRef.current) {
      process.env.NODE_ENV === "development" && console.error('Waveform container not found');
      setError('Waveform container not found');
      return;
    }

    // Initialize WaveSurfer
    let wavesurfer;
    try {
      wavesurfer = WaveSurfer.create({
        container: waveformRef.current,
        waveColor: blueColour,
        progressColor: goldColour,
        cursorColor: '#fff',
        barWidth: 2,
        barRadius: 3,
        cursorWidth: 1,
        height: 80,
        barGap: 2,
        normalize: true,
        backend: 'WebAudio',
        autoplay: false,
        interact: true,
        hideScrollbar: true,
        responsive: true,
        fillParent: true,
        minPxPerSec: 1,
        scrollParent: false,
        partialRender: true,
        pixelRatio: 1
      });
    } catch (err) {
      process.env.NODE_ENV === "development" && console.error('Error creating WaveSurfer instance:', err);
      setError('Error initializing audio player');
      return;
    }

    wavesurferRef.current = wavesurfer;

    // Add event listeners for error handling
    wavesurfer.on('error', (err) => {
      process.env.NODE_ENV === "development" && console.error('WaveSurfer error:', err);
    });

    // Track if component is mounted
    let isMounted = true;

    wavesurfer.on('ready', () => {
      process.env.NODE_ENV === "development" && console.log('WaveSurfer ready');
      if (!isMounted || !waveformRef.current) return;

      const handleWaveformClick = (e) => {
        if (!waveformRef.current) return;
        const rect = waveformRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const percentage = x / rect.width;
        wavesurfer.seekTo(percentage);
      };

      // Enable seeking on waveform click
      waveformRef.current.addEventListener('click', handleWaveformClick);
      
      // Store the handler for cleanup
      waveformRef.current.clickHandler = handleWaveformClick;
      
      if (isMounted) {
        setDuration(wavesurfer.getDuration());
      }
    });    
    // Add time update handler
    wavesurfer.on('audioprocess', (time) => {
      setCurrentTime(time);
      
      // Update lyrics if available
      if (currentLyrics) {
        const currentLyricObj = currentLyrics.find((lyric, index) => {
          const nextLyric = currentLyrics[index + 1];
          return time >= lyric.time && (!nextLyric || time < nextLyric.time);
        });
        
        if (currentLyricObj) {
          setCurrentLyric(currentLyricObj.text);
        } else {
          setCurrentLyric('');
        }
      }
    });

    wavesurfer.on('play', () => {
      setIsPlaying(true);
    });

    wavesurfer.on('pause', () => {
      setIsPlaying(false);
    });

    wavesurfer.on('seek', () => {
      if (isPlaying) {
        wavesurfer.play();
      }
    });

    // Handle song finish
    wavesurfer.on('finish', () => {
      if (songs && songs.length > 1) {
        handleNext();
      }
    });

    // Load audio file with retry mechanism
    const loadAudio = (retryCount = 0) => {
      if (!isMounted) return;
      
      if (!currentSongSource) {
        if (isMounted) setError('No audio source provided');
        return;
      }

      if (!wavesurferRef.current) {
        if (isMounted) setError('WaveSurfer not initialized');
        return;
      }

      // Check if the audio file exists by making a HEAD request
      fetch(currentSongSource, { method: 'HEAD' })
        .then(response => {
          if (!response.ok) {
            throw new Error(`Audio file not found: ${response.status}`);
          }
          return true;
        })
        .then(() => {
          try {
            const loadPromise = wavesurferRef.current.load(currentSongSource);
            
            // Handle the loading promise
            if (loadPromise && loadPromise.catch) {
              loadPromise.catch(err => {
                if (!isMounted) return;
                
                process.env.NODE_ENV === "development" && console.error('Error loading waveform:', err);
                if (retryCount < 3) {
                  // Retry after a delay with exponential backoff
                  setTimeout(() => loadAudio(retryCount + 1), Math.min(1000 * Math.pow(2, retryCount), 5000));
                } else {
                  setError('Unable to load audio player after multiple attempts');
                }
              });
            }
          } catch (err) {
            if (!isMounted) return;
            
            process.env.NODE_ENV === "development" && console.error('Error loading waveform:', err);
            if (retryCount < 3) {
              // Retry after a delay with exponential backoff
              setTimeout(() => loadAudio(retryCount + 1), Math.min(1000 * Math.pow(2, retryCount), 5000));
            } else {
              setError('Unable to load audio player after multiple attempts');
            }
          }
        })
        .catch(err => {
          process.env.NODE_ENV === "development" && console.error('Error checking audio file:', err);
          setError('Audio file not available yet. Please generate it first.');
        });
    };

    // Add error handler with retry mechanism
    wavesurfer.on('error', (err) => {
      if (!isMounted) return;
      
      process.env.NODE_ENV === "development" && console.error('WaveSurfer error:', err);
      if (err.name === 'AbortError') {
        // If it's an abort error, retry after a short delay
        setTimeout(loadAudio, 1000);
      } else {
        setError('Unable to load audio player: ' + (err.message || 'Unknown error'));
      }
    });

    // Start loading the audio with a small initial delay
    const loadTimeout = setTimeout(() => {
      if (isMounted) {
        loadAudio();
        // If isPlaying is true, we want to start playing once loaded
        if (isPlaying) {
          wavesurfer.on('ready', () => {
            if (isMounted) {
              wavesurfer.play();
            }
          });
        }
      }
    }, 100);

    // Clean up
    return () => {
      isMounted = false;
      if (waveformRef.current && waveformRef.current.clickHandler) {
        waveformRef.current.removeEventListener('click', waveformRef.current.clickHandler);
        delete waveformRef.current.clickHandler;
      }
      clearTimeout(loadTimeout);
      wavesurfer.destroy();
    };
  }, [currentSongSource, currentLyrics]);

  // Handle play state changes
  useEffect(() => {
    if (wavesurferRef.current) {
      if (isPlaying) {
        wavesurferRef.current.play();
      } else {
        wavesurferRef.current.pause();
      }
    }
  }, [isPlaying]);

  // Handle play/pause
  const togglePlay = () => {
    if (wavesurferRef.current) {
      wavesurferRef.current.playPause();
    }
  };
  const handleNext = () => {
    if (!songs || songs.length <= 1) return;
    
    // Update the song index
    setCurrentSongIndex((prev) => (prev + 1) % songs.length);
    
    // Keep playing state true since we want continuous playback
    setIsPlaying(true);
    
    // The useEffect for currentSongSource will handle loading and playing the new song
    // since we're maintaining isPlaying as true
  };

  const handlePrevious = () => {
    if (!songs || songs.length <= 1) return;
    
    // Update the song index
    setCurrentSongIndex((prev) => (prev - 1 + songs.length) % songs.length);
    
    // Keep playing state true since we want continuous playback
    setIsPlaying(true);
    
    // The useEffect for currentSongSource will handle loading and playing the new song
    // since we're maintaining isPlaying as true
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (wavesurferRef.current) {
      wavesurferRef.current.setVolume(newVolume);
      if (newVolume > 0) {
        setIsMuted(false);
      }
    }
    previousVolume.current = newVolume;
  };

  const toggleMute = () => {
    if (wavesurferRef.current) {
      if (!isMuted) {
        previousVolume.current = volume;
        wavesurferRef.current.setVolume(0);
        setVolume(0);
      } else {
        wavesurferRef.current.setVolume(previousVolume.current);
        setVolume(previousVolume.current);
      }
      setIsMuted(!isMuted);
    }
  };

  const handleSongSelect = (index) => {
    // Update the song index
    setCurrentSongIndex(index);
    
    // Keep playing state true to maintain consistent behavior with next/previous
    setIsPlaying(true);
    
    // The useEffect for currentSongSource will handle loading and playing the new song
    // since we're maintaining isPlaying as true
  };

  return (
    <div ref={containerRef} className="small-music-player" style={{
      padding: '10px',
      marginTop: '10px',
      borderRadius: '10px',
      width: '100%',
      maxWidth: '600px',
      ...containerStyle
    }}>
      <VisDiv style={{marginBottom: '10px'}} rules={[currentTitle]}>
       <h3>{currentTitle}</h3>
      </VisDiv>
    
      {error ? (
        <div style={{
          marginBottom: '10px',
          width: '100%',
          height: '80px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0,0,0,0.05)',
          borderRadius: '5px',
          color: '#666'
        }}>
          {error}
        </div>
      ) : (
        <div 
          ref={waveformRef} 
          style={{ 
            marginBottom: '10px',
            width: '100%',
            height: '80px',
            overflow: 'hidden'
          }} 
        />
      )}
      
      {/* Timer display */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: '0 10px',
        marginBottom: '20px',
        color: blueColour,
        fontSize: '14px',
        fontFamily: 'monospace'
      }}>
        <span>{formatTime(currentTime, secondsOnly)}</span>
        <span>{formatTime(duration, secondsOnly)}</span>
      </div>
      
      {/* Lyrics display */}
      {currentLyrics && (
        <div style={{
          textAlign: 'center',
          margin: '20px 0',
          padding: '10px',
          backgroundColor: "rgb(252,186,3, 0.2)",
          borderRadius: '5px',
          minHeight: '50px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <p style={{
            fontSize: '1.2em',
            color: currentLyric ? blueColour : '#999',
            margin: 0
          }}>
            {currentLyric || ""}
          </p>
        </div>
      )}
      
      {/* Controls */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', flexDirection: !screenSizeNotMobile ? 'column' : 'row' }}>
        <div style={{flex:1}}></div>
        <div style={{flex:1, display:"flex", flexDirection:'row'}}>
                {/* Playlist Controls */}
          <VisDiv rules={[songs && songs.length > 1]} style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            gap: '20px',
          }}>
            <button
              onClick={handlePrevious}
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              <FaStepBackward color={blueColour} size={30} />
            </button>
          </VisDiv>
          <button
            onClick={togglePlay}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              padding: '10px 20px',
              cursor: 'pointer',
              fontSize: '16px',
            }}
          >
            {isPlaying ? <FaPauseCircle color={goldColour} size={75}/> : <FaPlayCircle color={blueColour} size={75}/>}
          </button>
          <VisDiv rules={[songs && songs.length > 1]} style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            gap: '20px',
          }}>
            <button
              onClick={handleNext}
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              <FaStepForward color={blueColour} size={30} />
            </button>
          </VisDiv>
        </div>
        <div style={{flex:1, display: 'flex', justifyContent: 'center', alignItems: 'center', }}>
          <button
            onClick={toggleMute}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            {isMuted ? <FaVolumeMute color={blueColour} size={25}/> : <FaVolumeDown color={goldColour} size={25}/> }
          </button>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={volume}
            onChange={handleVolumeChange}
            className="volume-slider"
          />
        </div>
      </div>

      {/* Show songlist if songs array length > 1 */}
      {songs && songs.length > 1 && (
        <div style={{
          padding: '5px',
          maxHeight: '200px',
          overflowY: 'auto',
          marginTop: '20px'
        }}>
          {songs?.map((song, index) => (
            <div
              key={index}
              onClick={() => handleSongSelect(index)}
              style={{
                padding: '10px',
                cursor: 'pointer',
                backgroundColor: currentSongIndex === index ? `${goldColour}20` : 'transparent',
                borderRadius: '5px',
                marginBottom: '5px',
                transition: 'background-color 0.3s',
                color: currentSongIndex === index ? goldColour : blueColour,
                fontWeight: currentSongIndex === index ? 'bold' : 'normal',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = currentSongIndex === index ? `${goldColour}30` : `${blueColour}10`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = currentSongIndex === index ? `${goldColour}20` : 'transparent';
              }}
            >
              {song.title}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Helper function to format time in MM:SS or total seconds
const formatTime = (timeInSeconds, secondsOnly) => {
  if (secondsOnly) {
    return Math.floor(timeInSeconds).toString();
  }
  const minutes = Math.floor(timeInSeconds / 60);
  const seconds = Math.floor(timeInSeconds % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

export default LogMusicPlayer;
